import { ESAT_MODULE_LABELS } from './classifyEsatModule';

export const ESAT_OPTIONAL_ORDER = ['biology', 'chemistry', 'physics', 'mathematics_2'];

export const ESAT_PICKER_RESPONSE_REQUIRED = 'Select two further modules to continue.';

export function normalizeEsatHeritageExam(value) {
  const v = String(value || '').toUpperCase();
  if (v === 'NSAA' || v === 'ENGAA') return v;
  return null;
}

export function nsaaPickerEra(year) {
  if (year == null || year === '') return '2020';
  const y = Number(year);
  if (!Number.isFinite(y)) return '2020';
  if (y <= 2019) return '2016';
  return '2020';
}

export function isEsatPickerSelectionValid(selectedOptionalKeys, requiredCount = 2) {
  return Array.isArray(selectedOptionalKeys) && selectedOptionalKeys.length === requiredCount;
}

/** Response-Required copy that reflects how many modules are currently selected. */
export function getEsatPickerResponseRequiredMessage(selectedOptionalKeys, requiredCount = 2) {
  const count = Array.isArray(selectedOptionalKeys) ? selectedOptionalKeys.length : 0;
  const required = Number(requiredCount) || 2;
  if (required === 1) {
    if (count === 0) return 'Select one further module to continue.';
    if (count > 1) {
      return `You have selected ${count} further modules. Deselect ${count - 1} to leave exactly one selected.`;
    }
    return 'Select one further module to continue.';
  }
  if (count === 0) return 'Select two further modules to continue.';
  if (count === 1) return 'Select one more module to continue.';
  if (count > 2) {
    return `You have selected ${count} further modules. Deselect ${count - 2} to leave exactly two selected.`;
  }
  return ESAT_PICKER_RESPONSE_REQUIRED;
}

/** Maths 1 is always first. Optionals follow Biology, Chemistry, Physics, Mathematics 2. */
export function buildEsatSittingOrder(selectedOptionalKeys) {
  const chosen = new Set(selectedOptionalKeys);
  return ['mathematics_1', ...ESAT_OPTIONAL_ORDER.filter((key) => chosen.has(key))];
}

export function heritagePickerOptionalKeys({ heritageExam, year, optionals = {} } = {}) {
  const heritage = normalizeEsatHeritageExam(heritageExam);
  const present = ESAT_OPTIONAL_ORDER.filter((key) => optionals[key]?.id);
  if (heritage === 'NSAA' && nsaaPickerEra(year) === '2020') {
    return present.filter((key) => key !== 'mathematics_2');
  }
  return present;
}

export function getHeritagePickerConfig({ heritageExam, year, optionals = {} } = {}) {
  const heritage = normalizeEsatHeritageExam(heritageExam);
  if (heritage === 'NSAA') {
    const era = nsaaPickerEra(year);
    const optionalKeys = heritagePickerOptionalKeys({ heritageExam: 'NSAA', year, optionals });
    const optionalLabels = {
      biology: ESAT_MODULE_LABELS.biology,
      chemistry: ESAT_MODULE_LABELS.chemistry,
      physics: ESAT_MODULE_LABELS.physics,
      mathematics_2: 'Advanced Maths & Physics',
    };
    if (era === '2016') {
      return {
        compulsoryLabel: 'Mathematics',
        bodyCopy: 'Mathematics is required; choose two further modules.',
        factLine: 'Section 1: 54 multiple-choice questions, 80 minutes. Calculator not permitted.',
        requiredCount: 2,
        optionalKeys,
        optionalLabels,
      };
    }
    return {
      compulsoryLabel: 'Mathematics',
      bodyCopy: 'Mathematics is required; choose one further module.',
      factLine: 'Section 1: 40 multiple-choice questions, 60 minutes. Calculator not permitted.',
      requiredCount: 1,
      optionalKeys,
      optionalLabels,
    };
  }

  return {
    compulsoryLabel: ESAT_MODULE_LABELS.mathematics_1,
    bodyCopy: 'Mathematics 1 is required; choose two further modules.',
    factLine: null,
    requiredCount: 2,
    optionalKeys: ESAT_OPTIONAL_ORDER,
    optionalLabels: { ...ESAT_MODULE_LABELS },
  };
}

/**
 * @returns {{ skipPicker: boolean, persistKeys: string[] }}
 */
export function resolveEsatPickerCommit({ heritageExam, year, optionals = {} } = {}) {
  const heritage = normalizeEsatHeritageExam(heritageExam);
  const allowed = heritagePickerOptionalKeys({ heritageExam: heritage, year, optionals });

  if (heritage === 'ENGAA') {
    return { skipPicker: true, persistKeys: allowed };
  }

  if (heritage === 'NSAA') {
    if (allowed.length === 1) {
      return { skipPicker: true, persistKeys: allowed };
    }
    return { skipPicker: false, persistKeys: [] };
  }

  return { skipPicker: false, persistKeys: [] };
}
